import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, MapPin, Edit, Save, X, Users, BarChart3 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

const TeamLeadProfile = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    teamId: user?.team_id || '',
    region: user?.region || '',
  });

  const handleSave = async () => {
    if (!formData.name || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      // Update localStorage
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('roadserve_current_user', JSON.stringify(updatedUser));
      queryClient.invalidateQueries(['teamLeadProfile', user?.id]);
      alert('Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      alert('Error updating profile: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/teamlead/more')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
          <p className="text-gray-600 mt-1">Manage your profile and team information</p>
        </div>
      </div>

      {/* Profile Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-8 text-white">
        <div className="flex items-center space-x-6">
          <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-4xl">
              {user?.name?.charAt(0).toUpperCase() || 'T'}
            </span>
          </div>
          <div className="flex-1">
            <h2 className="text-3xl font-bold mb-2">{formData.name || 'Team Lead'}</h2>
            <p className="text-white/80 text-lg">{formData.email || 'No email'}</p>
            <p className="text-white/70 text-sm mt-1">Team Lead</p>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Personal Information</h3>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
            >
              <Edit size={18} />
              <span>Edit</span>
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    name: user?.name || '',
                    email: user?.email || '',
                    phone: user?.phone || '',
                    address: user?.address || '',
                    teamId: user?.team_id || '',
                    region: user?.region || '',
                  });
                }}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <X size={18} />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                <Save size={18} />
                <span>{isSaving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            ) : (
              <div className="flex items-center space-x-3">
                <User size={20} className="text-gray-400" />
                <span className="text-gray-900">{formData.name || 'N/A'}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <div className="flex items-center space-x-3">
              <Mail size={20} className="text-gray-400" />
              <span className="text-gray-900">{formData.email || 'N/A'}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            {isEditing ? (
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            ) : (
              <div className="flex items-center space-x-3">
                <Phone size={20} className="text-gray-400" />
                <span className="text-gray-900">{formData.phone || 'N/A'}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            {isEditing ? (
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            ) : (
              <div className="flex items-start space-x-3">
                <MapPin size={20} className="text-gray-400 mt-1" />
                <span className="text-gray-900">{formData.address || 'N/A'}</span>
              </div>
            )}
          </div>

          {formData.teamId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Team ID</label>
              <div className="flex items-center space-x-3">
                <Users size={20} className="text-gray-400" />
                <span className="text-gray-900">{formData.teamId}</span>
              </div>
            </div>
          )}

          {formData.region && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Region</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <div className="flex items-center space-x-3">
                  <MapPin size={20} className="text-gray-400" />
                  <span className="text-gray-900">{formData.region}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamLeadProfile;

